<div [class.read-only-mode]="isReadOnly()">
  <!-- Toast для сообщений -->
  <p-toast role="status" aria-live="polite" aria-atomic="true" />

  <!-- Live Region for Screen Readers -->
  <div role="status" aria-live="polite" aria-atomic="true" class="visually-hidden">
    {{ liveRegionMessage() }}
  </div>

  <!-- Page Header -->
  <section class="mb-4">
    <h1 class="text-3xl font-bold text-900 mb-2">Dati Specifici: Medico Legale</h1>
    <p class="text-600 m-0">Completa i dati professionali specifici per Medico Legale</p>
  </section>

  <!-- Instructions for accessibility -->
  @if (!isReadOnly()) {
    <div class="surface-100 border-round p-3 mb-4" role="region" aria-labelledby="form-instructions-title">
      <p id="form-instructions-title" class="text-sm text-700 m-0">
        <i class="pi pi-info-circle mr-2" aria-hidden="true"></i>
        <strong>Istruzioni:</strong> Compila tutti i campi obbligatori contrassegnati con asterisco (*). Nella sezione "Sede Operativa" indica se la sede legale è anche operativa; in caso contrario aggiungi almeno una sede operativa. Se sei dipendente pubblico, dovrai fornire informazioni aggiuntive.
      </p>
    </div>
  }

  <!-- SECTION 1: INFORMAZIONI SPECIFICHE -->
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="section-info">
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-info-circle text-3xl text-primary" aria-hidden="true"></i>
      <div>
        <h2 id="section-info" class="text-2xl font-semibold m-0 text-900">Informazioni Specifiche</h2>
        <p class="text-600 m-0 mt-1">Dati professionali e iscrizioni</p>
      </div>
    </div>

    <div class="formgrid grid">
      <!-- Activity Start Date -->
      <div class="field col-12 md:col-4">
        <label for="activity-start-date" class="font-semibold block mb-2">
          Inizio Attività dal
          <span class="text-red-500" aria-hidden="true">*</span>
          <span class="visually-hidden">obbligatorio</span>
        </label>
        <p-datepicker
          id="activity-start-date"
          [ngModel]="activityStartDate()"
          (ngModelChange)="activityStartDate.set($event); markFieldAsTouched('activityStartDate')"
          dateFormat="dd/mm/yy"
          [maxDate]="today"
          [showIcon]="true"
          [showButtonBar]="true"
          placeholder="Seleziona data"
          inputId="activity-start-date-input"
          [attr.aria-label]="'Data inizio attività'"
          [attr.aria-required]="true"
          [attr.aria-invalid]="(formSubmitted() || touchedFields.has('activityStartDate')) && !activityStartDate()"
          styleClass="w-full"
          (onBlur)="markFieldAsTouched('activityStartDate')"
          [disabled]="isReadOnly()"
        />
        @if ((formSubmitted() || touchedFields.has('activityStartDate')) && !activityStartDate()) {
          <small class="text-red-500" role="alert">Campo obbligatorio</small>
        }
      </div>

      <!-- Specialization -->
      <div class="field col-12 md:col-4">
        <label for="specialization" class="font-semibold block mb-2">
          Specializzazione
          <span class="text-red-500" aria-hidden="true">*</span>
          <span class="visually-hidden">obbligatorio</span>
        </label>
        <input
          id="specialization"
          type="text"
          pInputText
          [ngModel]="specialization()"
          (ngModelChange)="specialization.set($event)"
          placeholder="Inserisci specializzazione"
          class="w-full"
          [disabled]="isReadOnly()"
          (blur)="markFieldAsTouched('specialization')"
          [attr.aria-required]="true"
          [attr.aria-invalid]="(formSubmitted() || touchedFields.has('specialization')) && !specialization()"
        />
        @if ((formSubmitted() || touchedFields.has('specialization')) && !specialization()) {
          <small class="text-red-500" role="alert">Campo obbligatorio</small>
        }
      </div>

      <!-- Professional Order -->
      <div class="field col-12 md:col-4">
        <label for="professional-order" class="font-semibold block mb-2">
          Ordine di
          <span class="text-red-500" aria-hidden="true">*</span>
          <span class="visually-hidden">obbligatorio</span>
        </label>
        <input
          id="professional-order"
          type="text"
          pInputText
          [ngModel]="professionalOrder()"
          (ngModelChange)="professionalOrder.set($event)"
          placeholder="Inserisci ordine"
          class="w-full"
          [disabled]="isReadOnly()"
          (blur)="markFieldAsTouched('professionalOrder')"
          [attr.aria-required]="true"
          [attr.aria-invalid]="(formSubmitted() || touchedFields.has('professionalOrder')) && !professionalOrder()"
        />
        @if ((formSubmitted() || touchedFields.has('professionalOrder')) && !professionalOrder()) {
          <small class="text-red-500" role="alert">Campo obbligatorio</small>
        }
      </div>

      <!-- Order Registration Date -->
      <div class="field col-12 md:col-4">
        <label for="order-registration-date" class="font-semibold block mb-2">
          Data di iscrizione all'ordine
          <span class="text-red-500" aria-hidden="true">*</span>
          <span class="visually-hidden">obbligatorio</span>
        </label>
        <p-datepicker
          id="order-registration-date"
          [ngModel]="orderRegistrationDate()"
          (ngModelChange)="orderRegistrationDate.set($event); markFieldAsTouched('orderRegistrationDate')"
          dateFormat="dd/mm/yy"
          [maxDate]="today"
          [showIcon]="true"
          [showButtonBar]="true"
          placeholder="Seleziona data"
          inputId="order-registration-date-input"
          [attr.aria-label]="'Data iscrizione ordine'"
          [attr.aria-required]="true"
          [attr.aria-invalid]="(formSubmitted() || touchedFields.has('orderRegistrationDate')) && !orderRegistrationDate()"
          styleClass="w-full"
          (onBlur)="markFieldAsTouched('orderRegistrationDate')"
          [disabled]="isReadOnly()"
        />
        @if ((formSubmitted() || touchedFields.has('orderRegistrationDate')) && !orderRegistrationDate()) {
          <small class="text-red-500" role="alert">Campo obbligatorio</small>
        }
      </div>

      <!-- Public Employee -->
      <div class="field col-12 md:col-4">
        <div class="surface-50 border-round p-3">
          <div class="flex align-items-center gap-2">
            <p-checkbox
              inputId="is-public-employee"
              [binary]="true"
              [ngModel]="isPublicEmployee()"
              (ngModelChange)="onPublicEmployeeChange($event)"
              [disabled]="isReadOnly()"
              ariaLabel="Sei dipendente pubblico?"
            />
            <label for="is-public-employee" class="font-semibold cursor-pointer">
              Sei dipendente pubblico?
            </label>
          </div>
        </div>
      </div>

      <!-- INAIL Employee (conditional) -->
      @if (isPublicEmployee()) {
        <div class="field col-12 md:col-4">
          <div class="surface-50 border-round p-3">
            <fieldset class="border-none p-0 m-0">
              <legend class="font-semibold block mb-2">
                Dipendente INAIL
                <span class="text-red-500" aria-hidden="true">*</span>
                <span class="visually-hidden">obbligatorio</span>
              </legend>
              <div class="flex gap-3" role="group" aria-label="Dipendente INAIL">
                <div class="flex align-items-center gap-2">
                  <p-radiobutton
                name="isInailEmployee"
                [value]="true"
                [ngModel]="isInailEmployee()"
                (ngModelChange)="isInailEmployee.set($event)"
                inputId="is-inail-employee-yes"
                ariaLabel="Sì, dipendente INAIL"
                [disabled]="isReadOnly()"
              />
              <label for="is-inail-employee-yes" class="cursor-pointer">Sì</label>
            </div>
            <div class="flex align-items-center gap-2">
              <p-radiobutton
                name="isInailEmployee"
                [value]="false"
                [ngModel]="isInailEmployee()"
                (ngModelChange)="isInailEmployee.set($event)"
                inputId="is-inail-employee-no"
                ariaLabel="No, non dipendente INAIL"
                [disabled]="isReadOnly()"
              />
              <label for="is-inail-employee-no" class="cursor-pointer">No</label>
            </div>
          </div>
        </fieldset>
      </div>
    </div>
  }

      <!-- Companies Worked For -->
      <div class="field col-12">
        <label for="companies-worked" class="font-semibold block mb-2">
          Imprese per le quali lavora
        </label>
        <textarea
          id="companies-worked"
          pInputText
          rows="3"
          [ngModel]="assignedCompanies()"
          (ngModelChange)="onAssignedCompaniesChange($event)"
          placeholder="Inserisci le imprese separate da virgola (es: AXA Italia, AXA REAS)"
          class="w-full"
          (blur)="markFieldAsTouched('assignedCompanies')"
        ></textarea>
      </div>

      <!-- Operating Territories -->
      <div class="field col-12">
        <label for="operating-territories" class="font-semibold block mb-2">
          Territorio operativo
          <span class="text-red-500" aria-hidden="true">*</span>
          <span class="visually-hidden">obbligatorio</span>
        </label>
        <p-multiselect
          id="operating-territories"
          [ngModel]="operatingTerritories()"
          (ngModelChange)="operatingTerritories.set($event)"
          [options]="allProvincesOptions()"
          optionLabel="label"
          optionValue="value"
          placeholder="Seleziona Provincia"
          [filter]="true"
          [showToggleAll]="true"
          [showClear]="true"
          display="chip"
          [maxSelectedLabels]="allProvincesOptions().length"
          styleClass="w-full"
          (blur)="markFieldAsTouched('operatingTerritories')"
          [attr.aria-required]="true"
          [attr.aria-invalid]="(formSubmitted() || touchedFields.has('operatingTerritories')) && (!operatingTerritories() || operatingTerritories().length === 0)"
          [disabled]="isReadOnly()"
        />
        @if ((formSubmitted() || touchedFields.has('operatingTerritories')) && (!operatingTerritories() || operatingTerritories().length === 0)) {
          <small class="text-red-500" role="alert">Campo obbligatorio</small>
        }
      </div>
    </div>
  </section>

  <!-- SECTION 2: SEDE OPERATIVA (multi-sede) -->
  <app-operational-sites-section
    [sameAsLegal]="operatingSameAsLegal()"
    (sameAsLegalChange)="operatingSameAsLegal.set($event)"
    [sites]="operatingSites()"
    (sitesChange)="operatingSites.set($event)"
    [readonly]="isReadOnly()"
    [formSubmitted]="formSubmitted()"
  />

  <!-- I collaboratori sono ora gestiti nella sezione dedicata "Impiegati Amministrativi e Collaboratori" del wizard. -->

  <!-- Action Buttons (hidden in review mode) -->
  @if (!professionalIdInput()) {
  <div class="flex justify-content-end gap-3 mt-4">
    <button
      pButton
      type="button"
      label="Salva Bozza"
      icon="pi pi-save"
      class="p-button-outlined"
      [disabled]="isSaving()"
      [loading]="isSaving()"
      pTooltip="Salva i dati come bozza senza validazione completa"
      tooltipPosition="top"
      (click)="saveDraft()"
      [attr.aria-label]="'Salva i dati come bozza'"
    ></button>
    <button
      pButton
      type="button"
      label="Salva e continua"
      icon="pi pi-check"
      [disabled]="isSaving()"
      [loading]="isSaving()"
      pTooltip="Salva e valida i dati inseriti"
      tooltipPosition="top"
      (click)="saveAndContinue()"
      [attr.aria-label]="'Salva e valida i dati inseriti'"
    ></button>
  </div>
  }
</div>
